import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { base64StringToBlob } from 'blob-util';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import { NotificationContext } from 'src/components/general/feedback/hooks/use-show-notification';

const SettingsTextButton = styled.span`
  color: ${({ theme }) => theme.colors.secondaryGoldDark};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  line-height: 1.38;
  text-align: left;
`;

export const useTakeNativeCameraPhoto = () => {
  const { openNotification } = useContext(NotificationContext);
  const { t } = useTranslation(['common']);

  return async () => {
    const permissionState = (await Camera.requestPermissions()).camera;

    if (permissionState !== 'granted') {
      openNotification({
        title: t('common:cameraPermissionToast.title'),
        message: t('common:cameraPermissionToast.description'),
        type: 'warning',
        actionElements: (
          <SettingsTextButton
            onClick={() => {
              void NativeSettings.open({
                optionAndroid: AndroidSettings.ApplicationDetails,
                optionIOS: IOSSettings.App,
              });
            }}
          >
            {t('common:cameraPermissionToast.action')}
          </SettingsTextButton>
        ),
      });

      return;
    }

    let image: Photo | Error;

    try {
      image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
      });
    } catch (e) {
      image = e;
    }

    return match(image)
      .with(P.instanceOf(Error), () => {
        // Camera app canceled without taking a photo, can be ignored
        return null;
      })
      .otherwise((image) => {
        if (!image.base64String) {
          openNotification({
            type: 'error',
            title: t('common:nativeCameraPhotoErrorToast.title'),
            message: t('common:nativeCameraPhotoErrorToast.message'),
          });

          return;
        }

        const blob = base64StringToBlob(image.base64String, `image/${image.format}`);
        const now = Date.now();

        return new File([blob], `camera-file-${now.toString()}.${image.format}`, {
          lastModified: Date.now(),
          type: blob.type,
        });
      });
  };
};
